// Date arithmetic for the age/date tool.
//
// Everything works on local midnight (`new Date(y, m, d)`) rather than on the
// ISO string's UTC instant, so a user east of Greenwich does not see their
// birthday land a day early.

const MS_PER_DAY = 86400000;

/** Parses "YYYY-MM-DD" (what <input type="date"> gives) into a local Date. */
export const parseDateInput = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const [, year, month, day] = match.map(Number);
  const date = new Date(year, month - 1, day);
  // Rejects impossible dates like 2026-02-31, which JS would silently roll over.
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
};

export const toDateInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Whole days between two dates, ignoring the clock and DST shifts. */
export const daysBetween = (from, to) => Math.round((startOfDay(to) - startOfDay(from)) / MS_PER_DAY);

export const SHIFT_UNITS = ['days', 'weeks', 'months', 'years'];

/** Adds (or, with a negative amount, subtracts) a span from a date. */
export const shiftDate = (date, amount, unit) => {
  const value = Math.trunc(amount) || 0;
  const result = startOfDay(date);

  switch (unit) {
    case 'weeks':
      result.setDate(result.getDate() + value * 7);
      break;
    case 'months': {
      const day = result.getDate();
      result.setDate(1);
      result.setMonth(result.getMonth() + value);
      // Clamp so "31 January + 1 month" lands on the last day of February.
      result.setDate(Math.min(day, new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()));
      break;
    }
    case 'years': {
      const day = result.getDate();
      const month = result.getMonth();
      result.setDate(1);
      result.setFullYear(result.getFullYear() + value);
      result.setDate(Math.min(day, new Date(result.getFullYear(), month + 1, 0).getDate()));
      break;
    }
    default:
      result.setDate(result.getDate() + value);
  }

  return result;
};

/**
 * Calendar difference as years + months + days, the way people say their age.
 *
 * Counts whole months first, then measures the leftover days from that anchor
 * — subtracting day numbers directly goes wrong across months of unequal
 * length (31 January → 1 March is one month and one day, not one month
 * minus two days).
 */
export const calendarDiff = (from, to) => {
  const [early, late] = from <= to ? [from, to] : [to, from];

  let totalMonths = (late.getFullYear() - early.getFullYear()) * 12
    + (late.getMonth() - early.getMonth());
  if (late.getDate() < early.getDate()) totalMonths -= 1;

  const anchor = shiftDate(early, totalMonths, 'months');
  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12,
    days: daysBetween(anchor, late),
  };
};

/** Full age plus the totals and the next-birthday countdown the UI shows. */
export const ageDetails = (birthDate, today = new Date()) => {
  const birth = startOfDay(birthDate);
  const now = startOfDay(today);
  if (birth > now) return null;

  const { years, months, days } = calendarDiff(birth, now);
  const totalDays = daysBetween(birth, now);

  // February 29th falls back to March 1st in common years.
  let nextBirthday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate());
  if (nextBirthday < now) {
    nextBirthday = new Date(now.getFullYear() + 1, birth.getMonth(), birth.getDate());
  }

  return {
    years,
    months,
    days,
    totalDays,
    totalWeeks: Math.floor(totalDays / 7),
    totalMonths: years * 12 + months,
    totalHours: totalDays * 24,
    daysToBirthday: daysBetween(now, nextBirthday),
    birthWeekday: birth.getDay(),
    nextBirthday,
  };
};

/** Difference between two arbitrary dates, including a weekday/weekend split. */
export const dateDifference = (from, to) => {
  const totalDays = Math.abs(daysBetween(from, to));
  const { years, months, days } = calendarDiff(from, to);

  const [early] = from <= to ? [from, to] : [to, from];
  let weekdays = 0;
  for (let i = 0; i < totalDays; i += 1) {
    const day = new Date(early.getFullYear(), early.getMonth(), early.getDate() + i).getDay();
    if (day !== 0 && day !== 6) weekdays += 1;
  }

  return {
    totalDays,
    totalWeeks: Math.floor(totalDays / 7),
    remainderDays: totalDays % 7,
    years,
    months,
    days,
    weekdays,
    weekendDays: totalDays - weekdays,
  };
};
